/** P9.6 "Your reflection" (private) and, after the final rank, "Your commitment" (final round only): saved on blur, never shown to anyone else. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { GxCard } from "../theme/primitives";
import { GxLimitedText, INPUT_LIMITS } from "../theme/GxLimitedText";
import { reflectionQuestion, useReflection, type ReflectionContext, type ReflectionField, type SaveState } from "./reflection";

const SAVE_WORDS: Readonly<Record<SaveState, string>> = { idle: "Saves as you type", saving: "Saving…", saved: "Saved", failed: "Not saved. Try again." };

const AUTOSAVE_MS = 800;

function Field({ id, label, field, value, onSave, saveOnChange = false }: { readonly saveOnChange?: boolean; readonly id: string; readonly label: string; readonly field: ReflectionField; readonly value: string; readonly onSave: (field: ReflectionField, value: string) => void }) {
  const [text, setText] = useState(value);
  // The saved value flows back in only while the box is not being edited: no unsaved edits and not focused, so a save never rewrites what is being typed.
  const focused = useRef(false);
  const dirty = useRef(false);
  // A text past the maximum is kept on screen (still marked unsaved, so the saved value never rewrites it) and not saved.
  const over = text.length > INPUT_LIMITS.reflection;
  useEffect(() => { if (!focused.current && !dirty.current) setText(value); }, [value]);
  // `saveOnChange`: a long-lived box (the commitment) saves a moment after the last keystroke as well as on blur, so a reload never loses it.
  const save = useRef(onSave);
  save.current = onSave;
  useEffect(() => {
    if (!saveOnChange || over || text.trim() === value.trim()) return;
    const timer = setTimeout(() => { save.current(field, text); dirty.current = false; }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [text, value, saveOnChange, field, over]);
  return (
    <div className="gx-results-reflect-field" onFocus={() => { focused.current = true; }}>
      <GxLimitedText id={id} testId={`results.reflection.${field}.box`} fieldTestId={`results.reflection.${field}`} label={label} {...(field === "surprised" ? { labelTestId: "results.question" } : {})} max={INPUT_LIMITS.reflection} rows={4} placeholder={saveOnChange ? "A line or two is plenty." : undefined} value={text}
        onChange={(t) => { dirty.current = t.trim() !== value.trim(); setText(t); }}
        onBlur={() => { focused.current = false; if (over) return; if (text.trim() !== value.trim()) onSave(field, text); dirty.current = false; }} />
    </div>
  );
}

export function Reflection({ solo = false, teamId, roundId, roundNo, context }: { readonly solo?: boolean; readonly teamId: string; readonly roundId: string; readonly roundNo: number; readonly context: ReflectionContext }) {
  const { reflection, save, state } = useReflection(teamId, roundId);
  // One optional box. The round's own question (it names a decision or a card when one explains the year) is its prompt; the helper says it is private.
  const question = reflectionQuestion(roundNo, context, solo);
  return (
    <GxCard title="Reflection" testId="results.reflection">
      <div className="gx-card-b">
        <Field saveOnChange id={`surprised-${roundId}`} label={question} field="surprised" value={reflection?.surprised ?? ""} onSave={(f, v) => void save(f, v)} />
        <p className="gx-small gx-faint gx-results-reflect-foot"><span>{solo ? "Optional. Only you see this." : "Optional. Only you see this; your facilitator sees only that you wrote something."}</span><span role="status" className={state === "failed" ? "gx-rs-neg" : state === "saved" ? "gx-rs-pos" : undefined} data-testid="results.reflection.state">{SAVE_WORDS[state]}</span></p>
      </div>
    </GxCard>
  );
}

/** The commitment card; the solo finale puts its closing actions (the post-check, the link to Results) in the same card as `children`. */
export function Commitment({ teamId, roundId, children }: { readonly teamId: string; readonly roundId: string; readonly children?: ReactNode }) {
  const { reflection, save, state } = useReflection(teamId, roundId);
  return (
    <GxCard title="Your commitment" aside={<span className="gx-small gx-faint" role="status">{SAVE_WORDS[state]}</span>} testId="results.commitment">
      <div className="gx-card-b">
        <Field saveOnChange id={`commitment-${roundId}`} label="One thing you'll do differently at your desk" field="commitment" value={reflection?.commitment ?? ""} onSave={(f, v) => void save(f, v)} />
        <p className="gx-small gx-faint">Private to you.</p>
        {children}
      </div>
    </GxCard>
  );
}
